/**
 * InvitationsPage — admin-invite landing (P1 Tier-A, users.invite_admin). No
 * list endpoint exists, so the page is action-only: a lead paragraph, an
 * "Invite" button → InviteAdminDialog, and a receipt of the last-issued invite
 * (invitation_id + expires_at; the token itself never returns to the client).
 */
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { InviteAdminDialog } from "@/features/admin/invitations/InviteAdminDialog";
import type { InvitationResponse } from "@/features/admin/invitations/api";

export function InvitationsPage(): ReactElement {
  registerAdminI18n();
  const { t, i18n } = useTranslation(ADMIN_NS);
  const [open, setOpen] = useState(false);
  const [receipt, setReceipt] = useState<InvitationResponse | null>(null);

  const formatDate = (iso: string): string => {
    try {
      return new Intl.DateTimeFormat(i18n.language || "ru", { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
    } catch { return iso; }
  };

  return (
    <div data-testid="admin-invitations-page" className="flex flex-col gap-4">
      <h1 className="text-xl font-bold">{t("invitations.title")}</h1>
      <p className="max-w-prose text-sm text-[var(--g-secondary)]">{t("invitations.lead")}</p>

      <Button variant="primary" size="md" data-testid="invitations-open" onClick={() => setOpen(true)} className="min-h-[44px] w-fit">
        {t("invitations.invite")}
      </Button>

      {receipt ? (
        <div data-testid="invitations-receipt" className="max-w-md rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-4">
          <h2 className="text-sm font-semibold">{t("invitations.receipt.title")}</h2>
          <dl className="mt-2 flex flex-col gap-1 text-sm">
            <div className="flex justify-between gap-2">
              <dt className="text-[var(--g-secondary)]">{t("invitations.receipt.id")}</dt>
              <dd className="font-mono">{receipt.invitation_id}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-[var(--g-secondary)]">{t("invitations.receipt.expires")}</dt>
              <dd>{formatDate(receipt.expires_at)}</dd>
            </div>
          </dl>
        </div>
      ) : null}

      <InviteAdminDialog
        open={open}
        onClose={() => setOpen(false)}
        onCreated={(res) => { setReceipt(res); setOpen(false); toast.success(t("invitations.sentToast")); }}
      />
    </div>
  );
}

export default InvitationsPage;
